import { useSyncExternalStore } from "react";
import type { PlanePoint } from "./CartesianPlane";

// The plane's hover point lives outside React state: it changes on every pointer move, and only the
// few components that show or use it (the coordinates readout, the drawing preview) subscribe to it -
// the plane itself and its other layers don't re-render while the pointer moves.
export type PlanePointStore = {
  // The latest pointer position, pixels included - for handlers (e.g. zooming on the pointer).
  getLatest: () => PlanePoint | null;
  // What subscribers see: only replaced when the plane coordinate (x, y) changes, not on every pixel.
  getSnapshot: () => PlanePoint | null;
  set: (point: PlanePoint | null) => void;
  subscribe: (listener: () => void) => () => void;
};

export const createPlanePointStore = (): PlanePointStore => {
  const listeners = new Set<() => void>();
  let latest: PlanePoint | null = null;
  let snapshot: PlanePoint | null = null;

  return {
    getLatest: () => latest,
    getSnapshot: () => snapshot,
    set: (point) => {
      latest = point;

      if (point === snapshot || (point && snapshot && point.x === snapshot.x && point.y === snapshot.y)) {
        return;
      }

      snapshot = point;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    },
  };
};

const subscribeToNothing = () => () => {};
const getNoPoint = (): PlanePoint | null => null;

// The hover point, re-rendering the caller only when it moves to another plane coordinate. With
// enabled false it stays null and doesn't subscribe at all (e.g. once the shape is complete).
export const usePlanePoint = (store: PlanePointStore, enabled = true): PlanePoint | null =>
  useSyncExternalStore(enabled ? store.subscribe : subscribeToNothing, enabled ? store.getSnapshot : getNoPoint);
